import { Component } from 'react';
import { observer } from 'mobx-react';
import { makeObservable } from 'mobx';
import { Link } from 'react-router-dom';

import { folderStore } from '../services/folderStore.ts';

import type { FileResponseItem } from '../models.ts';
import { ImgFolder } from './Images/ImgFolder.tsx';
import { ImgTrash } from './Images/ImgTrash.tsx';
import { ImgUsers } from './Images/ImgUsers.tsx';
import FolderTree from './FolderTree.tsx';

type SidebarProps = {
  activeItem: 'files' | 'trash' | 'users';
  isOpen?: boolean;
  allFolders?: FileResponseItem[] | null;
  currentFolderId?: string | null;
  onGoToRoot?: () => void;
  onShowTrash?: () => void;
  onFolderClick?: (folderId: string) => void;
  onDelete?: (folder: FileResponseItem) => void;
  onPermanentDelete?: (folder: FileResponseItem) => void;
  onManageExpiration?: (folder: FileResponseItem) => void;
};

function buildActivePath(folderId: string | null | undefined, folders: FileResponseItem[]): Set<string> {
  const result = new Set<string>();
  if (!folderId) return result;
  let current: string | null = folderId;
  while (current) {
    result.add(current);
    const f = folders.find(x => x.id === current);
    current = f?.parentFolderId ?? null;
  }
  return result;
}

@observer
class Sidebar extends Component<SidebarProps> {
  constructor(props: SidebarProps) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    if (!this.props.allFolders && window.user) {
      folderStore.ensureLoaded();
    }
  }

  render() {
    const { activeItem, isOpen, allFolders, currentFolderId, onGoToRoot, onShowTrash, onFolderClick, onDelete, onPermanentDelete, onManageExpiration } = this.props;
    const user = window.user!;
    const folders = allFolders ?? folderStore.folders ?? [];
    const effectiveFolderId = activeItem === 'files' ? currentFolderId : null;
    const isAtRoot = activeItem === 'files' && !currentFolderId;
    const activePath = buildActivePath(effectiveFolderId, folders);

    return (
      <aside className={`sidebar${isOpen ? ' open' : ''}`} id="sidebar">
        <div className="sidebar-section">
          <div className="sidebar-title">Navigation</div>

          {onGoToRoot ? (
            <a
              href="#"
              className={`sidebar-item${isAtRoot ? ' active' : ''}`}
              onClick={(e) => { e.preventDefault(); onGoToRoot(); }}
            >
              <ImgFolder size={20} /> My Files
            </a>
          ) : (
            <Link to="/" className="sidebar-item">
              <ImgFolder size={20} /> My Files
            </Link>
          )}

          <FolderTree
            folders={folders}
            parentId={null}
            depth={1}
            activePath={activePath}
            currentFolderId={effectiveFolderId}
            onFolderClick={onFolderClick}
            onDelete={onDelete}
            onPermanentDelete={onPermanentDelete}
            onManageExpiration={onManageExpiration}
          />

          {onShowTrash ? (
            <a
              href="#"
              className={`sidebar-item${activeItem === 'trash' ? ' active' : ''}`}
              onClick={(e) => { 
                e.preventDefault(); 
                onShowTrash();
              }}
            >
              <ImgTrash size={20} /> Trash
            </a>
          ) : (
            <Link to="/trash" className={`sidebar-item${activeItem === 'trash' ? ' active' : ''}`}>
              <ImgTrash size={20} /> Trash
            </Link>
          )}
        </div>

        {user.roles?.includes('admin') && (
          <div className="sidebar-section" id="adminNav">
            <div className="sidebar-title">Admin</div>
            <Link to="/users" className={`sidebar-item${activeItem === 'users' ? ' active' : ''}`}>
              <ImgUsers size={20} /> Users
            </Link>
          </div>
        )}
      </aside>
    );
  }
}

export { Sidebar };
